/**
 * Galigeo Viewer Lit Components
 * Entry point for component loader
 */

import {
  loadComponent,
  preloadComponent,
  getAvailableComponents,
  isComponentLoaded,
  type LoadComponentOptions,
} from './loader';

import { DataService } from './services';

// Re-export types for external use
export type { LoadComponentOptions };
export type { MappingField, MappingMatch, FilterCountry, MappingWizardOptions } from './features/mapping-wizard-modal';
export type { ThematicWizardOptions } from './features/thematic-wizard/thematic-wizard-modal';
export type { ThematicFilterOptions, ThematicFilterLayer } from './features/thematic-filter-modal';
export type { AddDataOptions, AddDataRoles } from './features/data/add-data-modal';
export type { SqlQueryBuilderOptions } from './features/data/sql-query-builder';
export type { SheetSelectorOptions, SheetInfo } from './features/data/sheet-selector-modal';
export type { MeasurePickerOptions } from './features/data/measure-picker-modal';
export { TableData, type TableColumn, type TableRow } from './models/table-data';

// Re-export services
export * from './services';

// Extend the global Galigeo namespace
declare global {
  interface Window {
    Galigeo: {
      /**
       * Loads a feature component dynamically and renders it in the specified container
       * @example
       * const modal = await Galigeo.loadComponent('modal', document.body);
       * modal.open({ title: 'Hello' });
       */
      loadComponent: typeof loadComponent;

      /**
       * Preloads a component without rendering it
       * @example
       * await Galigeo.preloadComponent('modal');
       */
      preloadComponent: typeof preloadComponent;

      /**
       * Returns list of available component names
       */
      getAvailableComponents: typeof getAvailableComponents;

      /**
       * Checks if a component is already loaded
       */
      isComponentLoaded: typeof isComponentLoaded;

      /**
       * Data service for REST API calls
       */
      DataService: typeof DataService;

      [key: string]: unknown;
    };
  }
}

// Ensure Galigeo namespace exists
window.Galigeo = window.Galigeo || {} as Window['Galigeo'];

// Register loader functions
window.Galigeo.loadComponent = loadComponent;
window.Galigeo.preloadComponent = preloadComponent;
window.Galigeo.getAvailableComponents = getAvailableComponents;
window.Galigeo.isComponentLoaded = isComponentLoaded;

// Register services
window.Galigeo.DataService = DataService;

// Load theme CSS variables from server
function getBaseUrl(): string {
  const url = window.location.origin + window.location.pathname;
  for (const dir of ['/viewer', '/oauth', '/saml', '/extension']) {
    const idx = url.indexOf(dir);
    if (idx > 0) return url.substring(0, idx) + '/';
  }
  for (const dir of ['/Galigeo', '/galigeo']) {
    const idx = url.indexOf(dir);
    if (idx > 0) return url.substring(0, idx + 8) + '/';
  }
  const lastIdx = url.lastIndexOf('/');
  return url.substring(0, lastIdx) + '/';
}

async function loadThemeVariables(): Promise<void> {
  try {
    // The theme is portal-wide (not map-specific) so it always lives at the
    // PortalEndPoint route, regardless of whether a `url` query param is set.
    const themeUrl = getBaseUrl() + 'feature/portal/theme/variables';

    const response = await fetch(themeUrl);
    if (response.ok && response.status !== 204) {
      const css = await response.text();
      if (css.trim()) {
        const style = document.createElement('style');
        style.id = 'ggo-theme-variables';
        style.textContent = css;
        document.head.appendChild(style);
      }
    }
  } catch (e) {
    console.warn('[Galigeo Components] Failed to load theme variables:', e);
  }
}

loadThemeVariables();

// Log component initialization
console.log('[Galigeo Components] Loader initialized. Available components:', getAvailableComponents());
